/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useState, useCallback } from 'react';
import { useIntl } from 'react-intl-next';
import { cssMap, cx, jsx } from '@atlaskit/css';
import Tooltip from '@atlaskit/tooltip';
import { N700 } from '@atlaskit/theme/colors';
import UnlinkIcon from '@atlaskit/icon/core/link-broken';
import { Box, Inline, Text } from '@atlaskit/primitives/compiled';
import { Anchor } from '@atlaskit/primitives/compiled';
import CogIcon from '@atlaskit/icon/core/migration/settings';
import { token } from '@atlaskit/tokens';
import { SpotlightManager } from '@atlaskit/onboarding';

import { type Integration } from '../../types';
import { useAnalytics } from '../../analytics';
import { messages } from '../i18n';
import { useInvitePeopleContext } from '../InvitePeople';
import { PLATFORM_SLACK_SERVICE } from './constants';
import { useIntegrationComponents } from './useIntegrationComponents';
import { useThirdPartyState } from './ThirdPartyContext';
import { ThirdPartyAnalytics } from './ThirdPartyAnalytics';

const MAX_INTEGRATIONS_WITH_LABEL = 3;

const styles = cssMap({
	wrapper: {
		marginTop: token('space.100', '8px'),
	},
	fieldSet: {
		display: 'contents',
	},
	connectedIntegrationWrapper: {
		borderRadius: token('radius.xsmall', '2px'),
		marginBlock: token('space.050', '4px'),
		marginInline: token('space.075', '6px'),
		height: '16px',
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'center',
		color: token('color.text.subtlest', '#626F86'),
		font: token(
			'font.body.UNSAFE_small',
			'normal 400 12px/16px ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", Ubuntu, system-ui, "Helvetica Neue", sans-serif',
		),
		backgroundColor: token('elevation.surface', '#FFFFFF'),
		textDecoration: 'none',
		'&:hover': {
			textDecoration: 'none',
			backgroundColor: token('color.background.danger.hovered', '#FFD5D2'),
		},
	},
	connectedPlatformSlackServiceWrapper: {
		'&:hover': {
			backgroundColor: token('color.background.neutral.hovered', 'rgba(9,30,66,0.14)'),
		},
	},
	connectedIntegrationText: {
		color: token('color.text', '#172B4D'),
		marginInline: token('space.050', '4px'),
	},
});

const ConnectedIntegration: React.FC<{
	integration: string;
	shouldShowLabel?: boolean;
}> = ({ integration, shouldShowLabel = true }) => {
	const analytics = useAnalytics(ThirdPartyAnalytics);
	const { onDisconnectProvider } = useThirdPartyState();
	const { orgId } = useInvitePeopleContext();
	const [hover, setHover] = useState(false);

	let { icon } = useIntegrationComponents(integration as Integration);
	const { connectButtonText, toolTip } = useIntegrationComponents(integration as Integration);

	if (hover) {
		icon =
			integration === PLATFORM_SLACK_SERVICE ? (
				<CogIcon
					color={token('color.icon', N700)}
					LEGACY_size={'small'}
					size={'small'}
					spacing="compact"
					label={'settings'}
					key={integration}
				/>
			) : (
				<UnlinkIcon
					color={token('color.icon', N700)}
					LEGACY_size={'small'}
					size={'small'}
					spacing="compact"
					label={'unlink'}
					key={integration}
				/>
			);
	}

	const onMouseOver = useCallback(() => {
		if (!hover) {
			setHover(true);
		}
	}, [hover]);

	const onMouseOut = useCallback(() => {
		if (hover) {
			setHover(false);
		}
	}, [hover]);

	const onClick = useCallback(() => {
		analytics.integrationSettingsButtonClicked(integration, orgId);

		onDisconnectProvider(integration);
	}, [integration, analytics, onDisconnectProvider, orgId]);

	const integrationComponent = (
		<Anchor
			testId={`testId-invite-people-third-party-${integration}`}
			// eslint-disable-next-line styled-components-a11y/mouse-events-have-key-events
			onMouseOver={onMouseOver}
			// eslint-disable-next-line styled-components-a11y/mouse-events-have-key-events
			onMouseOut={onMouseOut}
			onClick={onClick}
			href="#"
			xcss={cx(
				styles.connectedIntegrationWrapper,
				integration === PLATFORM_SLACK_SERVICE && styles.connectedPlatformSlackServiceWrapper,
			)}
		>
			<Tooltip position="bottom" content={toolTip}>
				<span>{icon}</span>
			</Tooltip>
			{shouldShowLabel && <span css={styles.connectedIntegrationText}>{connectButtonText}</span>}
		</Anchor>
	);

	return integrationComponent;
};

const ThirdPartyConnectedTo = () => {
	const { formatMessage } = useIntl();
	const { integrations, updateIntegrations } = useThirdPartyState();

	return (
		<SpotlightManager>
			<Inline alignBlock="center" alignInline="start" xcss={styles.wrapper}>
				<fieldset css={styles.fieldSet}>
					<Box as="legend">
						<Text size="small" weight="regular">
							{formatMessage(messages.thirdPartyConnectedTo)}
						</Text>
					</Box>
					{[...integrations].map((integration) => {
						if (updateIntegrations.noScope.has(integration)) {
							return null;
						}
						return (
							<ConnectedIntegration
								integration={integration}
								key={integration}
								shouldShowLabel={integrations.size <= MAX_INTEGRATIONS_WITH_LABEL}
							/>
						);
					})}
				</fieldset>
			</Inline>
		</SpotlightManager>
	);
};

export default ThirdPartyConnectedTo;
